import React, { Component, createRef, Fragment } from 'react';
import { arrayOf, bool, number, shape, string, object } from 'prop-types';
import classnames from 'classnames';
import { HAS_SAVE_IMAGE } from 'constants/site';
import Image from 'components/Image';
import LazyLoad from 'components/LazyLoad';
import Arrow from 'components/Icons/Arrow';
import Close from 'components/Icons/Close';
import Grid from 'components/Icons/Grid';
import Lightbox from 'components/Lightbox';
import PrismEntryButton from 'components/PrismEntryButton';
import SlideGalleryImage from 'components/SlideGalleryImage';
import SaveImageToolBar from 'components/SaveImageToolBar';
import { MQ_LARGE, MQ_MEDIUM } from 'constants/mq';
import { baseStyles, slideStyles, thumbnailStyles } from './styles';

const LAZY_LOAD_OFFSET = 50; // px

const isInViewport = (offset = 0, element) => {
  if (!element) return false;
  const { top } = element.getBoundingClientRect();
  return top + offset >= 0 && top - offset <= window.innerHeight;
};

const IMAGE_SPECS = {
  preview: {
    maxSizeWidth: 382,
    sourceSizes: [{ layoutWidth: { units: 'px', value: 320 } }]
  },
  slider: {
    maxSizeWidth: 845, // 2/3rds of 1280
    sourceSizes: [
      {
        media: MQ_LARGE,
        layoutWidth: { units: 'px', value: 845 }
      },
      {
        media: MQ_MEDIUM,
        layoutWidth: { units: 'px', value: 696 }
      },
      { layoutWidth: { units: 'px', value: 382 } }
    ]
  },
  dotIndicatorSlider: {
    maxSizeWidth: 480,
    sourceSizes: [{ layoutWidth: { units: 'px', value: 480 } }]
  }
};

const getAspectRatio = image =>
  image.originalWidth && image.originalHeight
    ? image.originalWidth / image.originalHeight
    : undefined;

const createImageProps = (aspectRatio, img, variant = 'slider') => ({
  ...img,
  alt: img.alt || 'Gallery Image',
  className: `SlideGallery__slide--original`,
  maxSize: {
    width: IMAGE_SPECS[variant].maxSizeWidth,
    aspectRatio
  },
  sourceSizes: IMAGE_SPECS[variant].sourceSizes
});

class SlideGallery extends Component {
  constructor(props) {
    super(props);
    this.state = {
      activeSlideIdx: 0,
      thumbnailView: false,
      left: 0,
      originalOffsetX: 0,
      touchStartX: 0,
      isBeingTouched: false,
      toggleFullScreen: false
    };

    this.handlePrevArrow = this.handlePrevArrow.bind(this);
    this.handleNextArrow = this.handleNextArrow.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.navigateToSlide = this.navigateToSlide.bind(this);
    this.renderSlides = this.renderSlides.bind(this);
    this.renderPrism = this.renderPrism.bind(this);
    this.renderThumbnails = this.renderThumbnails.bind(this);
    this.toggleThumbnails = this.toggleThumbnails.bind(this);
    this.toggleFullScreen = this.toggleFullScreen.bind(this);

    this.galleryEl = createRef();

    this.lastSwipeDirection = undefined;
  }

  componentDidMount() {
    if (this.galleryEl.current) {
      // if the gallery is in view we want to focus it so we can use the arrow keys for navigation
      if (isInViewport(200, this.galleryEl.current)) {
        // prevent the window from scrolling to the focused gallery in modern browsers Chrome, Edge, Safari
        this.galleryEl.current.focus({
          preventScroll: true
        });
        // While we have support for IE11 we need this to prevent scroll after focus
        window.scrollTo(window.scrollX, window.scrollY);
      }
    }
  }

  componentDidUpdate(prevState) {
    const { thumbnailView } = this.state;
    if (
      !thumbnailView &&
      prevState.activeSlideIdx !== this.state.activeSlideIdx
    ) {
      // force a check everytime a slide changes
      LazyLoad.runForceCheck();
    }
  }

  navigateToSlide(e, index) {
    e.preventDefault();
    this.setState({
      thumbnailView: false,
      activeSlideIdx: index
    });
  }

  handleNextArrow() {
    const { images } = this.props;
    const { activeSlideIdx } = this.state;
    if (images[activeSlideIdx + 1] !== undefined) {
      this.setState({
        activeSlideIdx: activeSlideIdx + 1
      });
    } else {
      this.setState({
        activeSlideIdx: 0
      });
    }
  }

  handlePrevArrow() {
    const { images } = this.props;
    const { activeSlideIdx } = this.state;
    if (activeSlideIdx > 0) {
      this.setState({
        activeSlideIdx: activeSlideIdx - 1
      });
    } else {
      this.setState({
        activeSlideIdx: images.length - 1
      });
    }
  }

  handleKeyDown(e) {
    if (e.key === 'ArrowLeft') {
      this.handlePrevArrow();
    } else if (e.key === 'ArrowRight') {
      this.handleNextArrow();
    }
  }

  toggleThumbnails(e) {
    e.preventDefault();

    this.setState(state => ({
      thumbnailView: !state.thumbnailView
    }));
  }

  toggleFullScreen(e) {
    e.preventDefault();
    this.setState(state => ({
      toggleFullScreen: !state.toggleFullScreen
    }));
  }

  handleMove(clientX, clientY) {
    const { originalOffsetX, touchStartX, touchStartY } = this.state;
    if (this.state.isBeingTouched) {
      const deltaX = clientX - touchStartX + originalOffsetX;
      const deltaY = clientY - touchStartY + originalOffsetX;

      if (deltaX < 0) {
        this.lastSwipeDirection = 'forward';
      } else if (deltaX > 0) {
        this.lastSwipeDirection = 'back';
      }

      if (Math.abs(deltaX) < 80) {
        this.lastSwipeDirection = undefined;
        return;
      }

      if (Math.abs(deltaY) > 80) {
        this.lastSwipeDirection = 'vertical';
        return;
      }

      this.setState({
        left: deltaX
      });
    }
  }

  handleStart(clientX, clientY) {
    this.setState({
      originalOffsetX: this.state.left,
      touchStartX: clientX,
      touchStartY: clientY,
      isBeingTouched: true
    });
  }

  handleEnd() {
    if (this.lastSwipeDirection === 'forward') {
      this.handleNextArrow();
    } else if (this.lastSwipeDirection === 'back') {
      this.handlePrevArrow();
    }
    this.lastSwipeDirection = undefined;
    this.setState({
      left: 0,
      touchStartX: 0,
      isBeingTouched: false
    });
  }

  handleTouchStart(touchStartEvent) {
    this.handleStart(
      touchStartEvent.targetTouches[0].clientX,
      touchStartEvent.targetTouches[0].clientY
    );
  }

  handleTouchMove(touchMoveEvent) {
    this.handleMove(
      touchMoveEvent.targetTouches[0].clientX,
      touchMoveEvent.targetTouches[0].clientY
    );
  }

  handleTouchEnd() {
    this.handleEnd();
  }

  handleMouseDown(mouseDownEvent) {
    if (mouseDownEvent.nativeEvent.which === 3) {
      // don't count right clicks
      return;
    }
    mouseDownEvent.preventDefault();
    this.handleStart(mouseDownEvent.clientX, mouseDownEvent.clientY);
  }

  handleMouseMove(mouseMoveEvent) {
    this.handleMove(mouseMoveEvent.clientX, mouseMoveEvent.clientY);
  }

  handleMouseUp() {
    this.handleEnd();
  }

  renderSlideNumber() {
    const { activeSlideIdx } = this.state;
    const { images, variant } = this.props;

    return (
      <div className="SlideGallery__slide-number">
        {variant === 'dotIndicatorSlider' &&
          images.map((img, i) => (
            <span
              key={img.url}
              className={classnames({
                'SlideGallery__slide-indicator': true,
                'SlideGallery__slide-indicator--active': activeSlideIdx === i
              })}
            />
          ))}
        {variant !== 'dotIndicatorSlider' && (
          <Fragment>
            {activeSlideIdx + 1} / {images.length}
          </Fragment>
        )}
      </div>
    );
  }

  renderThumbnails() {
    const { activeSlideIdx } = this.state;
    const { images } = this.props;

    return (
      <div
        className="SlideGallery__thumbnail"
        data-gtm-gallery-type="slide-gallery"
        data-gtm-slide-idx={activeSlideIdx + 1}
      >
        <style jsx>{thumbnailStyles}</style>
        <div className="SlideGallery__thumbnail-controls">
          <a href="#" onClick={this.toggleThumbnails}>
            <Close />
          </a>
          {this.renderSlideNumber()}
        </div>
        <ul>
          {images.map((thumbnail, index) => {
            const aspectRatio = getAspectRatio(thumbnail);
            const imageProps = createImageProps(
              aspectRatio,
              thumbnail,
              'preview'
            );
            return (
              <li key={thumbnail.url}>
                <a
                  href={`#slide-${index}`}
                  onClick={e => this.navigateToSlide(e, index)}
                >
                  <div className="SlideGallery__thumbnail--placeholder">
                    <LazyLoad overflow offset={LAZY_LOAD_OFFSET} once>
                      <Image {...imageProps} />
                    </LazyLoad>
                  </div>
                </a>

                {this.renderPrism(thumbnail.hashedPublicId)}
                <div
                  className="SlideGallery__thumbnail--caption"
                  dangerouslySetInnerHTML={{
                    __html: thumbnail.caption
                      ? thumbnail.caption.replace(thumbnail.credit, '')
                      : ''
                  }}
                />
              </li>
            );
          })}
        </ul>
      </div>
    );
  }

  renderPrism(imageId) {
    const { hasPrism, post } = this.props;

    if (!hasPrism || !post) {
      return null;
    }
    return (
      <div className="SlideGallery__slide-prism">
        <PrismEntryButton imageId={imageId} entryPageUrl={post.canonical} />
      </div>
    );
  }

  renderArrows() {
    const { activeSlideIdx } = this.state;
    const { images } = this.props;

    return (
      <Fragment>
        <button
          className={classnames(
            'SlideGallery__control-icon',
            activeSlideIdx > 0 ? '' : 'SlideGallery__control-icon--hidden'
          )}
          onClick={this.handlePrevArrow}
          aria-label="Previous image"
        >
          <Arrow rotate={180} />
        </button>

        <button
          className={classnames(
            'SlideGallery__control-icon',
            activeSlideIdx + 1 < images.length
              ? ''
              : 'SlideGallery__control-icon--hidden'
          )}
          onClick={this.handleNextArrow}
          aria-label="Next image"
        >
          <Arrow />
        </button>
      </Fragment>
    );
  }

  renderSlides() {
    const { activeSlideIdx, left } = this.state;
    const {
      post,
      coverMedia,
      images,
      variant,
      hasPrism,
      hideImageActions,
      hasLightbox
    } = this.props;
    const activeSlide = images.length > 0 ? images[activeSlideIdx] : undefined;

    if (!activeSlide) return null;

    const credit = activeSlide.credit || '';

    const hasImageActions = !hideImageActions && HAS_SAVE_IMAGE;

    return (
      <div
        className={classnames(
          'SlideGallery__slide-image',
          activeSlide.originalWidth > activeSlide.originalHeight
            ? 'SlideGallery__slide-image--landscape'
            : 'SlideGallery__slide-image--portait',
          {
            'SlideGallery__slide-image--cover': coverMedia,
            [`SlideGallery__slide-image--${variant}`]: variant
          }
        )}
      >
        <style jsx>{slideStyles}</style>
        <nav className="SlideGallery__cover-controls">
          {coverMedia ? this.renderArrows() : null}
        </nav>
        {/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */}
        <ul
          onTouchStart={touchStartEvent =>
            this.handleTouchStart(touchStartEvent)
          }
          onTouchMove={touchMoveEvent => this.handleTouchMove(touchMoveEvent)}
          onTouchEnd={() => this.handleTouchEnd()}
          // The following event handlers are for mouse compatibility:
          onMouseDown={mouseDownEvent => this.handleMouseDown(mouseDownEvent)}
          onMouseMove={mouseMoveEvent => this.handleMouseMove(mouseMoveEvent)}
          onMouseUp={() => this.handleMouseUp()}
        >
          {images.map((image, index) => {
            const isActiveSlide = activeSlideIdx === index;
            const imageProps = createImageProps(
              getAspectRatio(image),
              image,
              variant
            );

            return (
              <SlideGalleryImage
                key={image.url}
                left={left}
                index={index}
                canonical={post ? post.canonical : undefined}
                isActiveSlide={isActiveSlide}
                isPinnable={image.isPinnable}
                image={imageProps}
                toggleFullScreen={
                  hasLightbox ? this.toggleFullScreen : undefined
                }
              />
            );
          })}
        </ul>
        <div className="SlideGallery__slide-info">
          {post && hasImageActions && (
            <SaveImageToolBar
              post={post}
              isPinnable={activeSlide.isPinnable}
              hashedPublicId={activeSlide.hashedPublicId}
              hidePrism={!hasPrism}
              hasPrism={hasPrism}
              imgProps={{
                ...createImageProps(getAspectRatio(activeSlide), activeSlide),
                className: 'SlideGallery__image--save-modal',
                alt: activeSlide.caption
              }}
            />
          )}
          {variant !== 'dotIndicatorSlider' && (
            <div
              className="SlideGallery__slide-cap"
              dangerouslySetInnerHTML={{
                __html: credit
              }}
            />
          )}
        </div>

        {variant === 'dotIndicatorSlider' ? (
          <Fragment>
            {this.renderSlideNumber()}
            <div className="SlideGallery__slide-navigation">
              {this.renderArrows()}
            </div>
            <div
              className="SlideGallery__slide-caption"
              dangerouslySetInnerHTML={{
                __html: activeSlide.caption
                  ? `${activeSlide.caption.replace(credit, '')} ${
                      credit ? `(Image credit: ${credit})` : ''
                    }`
                  : ''
              }}
            />
          </Fragment>
        ) : (
          <div className="SlideGallery__slide-meta">
            <div className="SlideGallery__slide-navigation">
              {this.renderSlideNumber()}
              <nav className="SlideGallery__controls">
                <div className="SlideGallery_slide-thumbnail-view">
                  <a
                    href="#grid-view"
                    onClick={this.toggleThumbnails}
                    aria-label="Grid view"
                  >
                    <Grid />
                  </a>
                </div>
                {!coverMedia ? this.renderArrows() : null}
              </nav>
            </div>

            <div
              className="SlideGallery__slide-caption"
              dangerouslySetInnerHTML={{
                __html: activeSlide.caption
                  ? activeSlide.caption.replace(credit, '')
                  : ''
              }}
            />
          </div>
        )}
      </div>
    );
  }

  render() {
    const { coverMedia, variant } = this.props;
    const { thumbnailView, toggleFullScreen } = this.state;
    return (
      <div
        className={classnames({
          SlideGallery: true,
          cover: coverMedia,
          'SlideGallery--thumbnails-active': this.state.thumbnailView,
          [`SlideGallery--${variant}`]: variant
        })}
        onKeyDown={e => this.handleKeyDown(e)}
        role="button"
        tabIndex="0"
        ref={this.galleryEl}
      >
        <style jsx>{baseStyles}</style>

        <div className="SlideGallery__slides">
          {this.renderSlides()}
          {thumbnailView && this.renderThumbnails()}
        </div>
        {toggleFullScreen && (
          <Lightbox
            images={this.props.images}
            toggleFullScreen={this.toggleFullScreen}
            currentIndex={this.state.activeSlideIdx}
            navFunctions={{
              handlePrevArrow: this.handlePrevArrow,
              handleNextArrow: this.handleNextArrow
            }}
          />
        )}
      </div>
    );
  }
}

SlideGallery.propTypes = {
  hasPrism: bool,
  coverMedia: bool,
  post: shape({
    ads: shape({
      targeting: object
    }),
    canonical: string
  }),
  images: arrayOf(
    shape({
      url: string,
      width: number,
      height: number,
      format: string,
      alt: string,
      caption: string,
      isPinnable: bool
    })
  ).isRequired,
  variant: string,
  hideImageActions: bool,
  hasLightbox: bool
};

SlideGallery.defaultProps = {
  coverMedia: false,
  post: undefined,
  hasPrism: false,
  variant: undefined,
  hideImageActions: false,
  hasLightbox: false
};

export default SlideGallery;
